import React, { useState, useMemo, useEffect } from "react";
import {
  IconButton,
  Popover,
  Box,
  TextField,
  Button,
  Stack,
  Tooltip,
  Badge,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import httpRequest from "../utils/httpRequest";

const initialFilterState = {
  name: "",
  mobile: "",
  email: "",
  address: "",
  gst: "",
  location: "",
  account_number: "",
};

const CustomerFilterPopover = ({ filters, setFilters, locations, onNextAccountNumber }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [localFilters, setLocalFilters] = useState(
    filters || initialFilterState
  );
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const handleOpen = (e) => {
    setAnchorEl(e.currentTarget);
    setLocalFilters(filters);
  };
  const handleClose = () => setAnchorEl(null);

  const applyFilters = () => {
    const cleanedFilters = Object.fromEntries(
      Object.entries(localFilters).filter(
        ([_, value]) => value !== null && value !== ""
      )
    );
    setFilters(cleanedFilters);
    handleClose();
  };

  const resetFilters = () => {
    setLocalFilters(initialFilterState);
    setFilters(initialFilterState);
    handleClose();
  };

  const handleNextAccountNumber = async () => {
    if (!filters.account_number) {
      return;
    }

    try {
      const response = await httpRequest(
        `customers/next-account-number?currentAccountNumber=${filters.account_number}`,
        'GET'
      );
      
      // Update the account number filter to the next one
      const newFilters = {
        ...filters,
        account_number: response.nextAccountNumber
      };
      setFilters(newFilters);
      
      // Call the parent callback if provided
      if (onNextAccountNumber) {
        onNextAccountNumber(response);
      }
    } catch (error) {
      console.error('Error getting next account number:', error);
    }
  };

  const activeFilterCount = useMemo(() => {
    return Object.values(filters || {}).filter(
      (val) => val !== "" && val !== null
    ).length;
  }, [filters]);

  // Add keyboard shortcut for next account number
  useEffect(() => {
    const handleKeyPress = (event) => {
      if (filters.account_number && event.key === 'n' && !event.ctrlKey && !event.metaKey) {
        event.preventDefault();
        handleNextAccountNumber();
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [filters.account_number]);

  return (
    <>
      <Tooltip title="Filter Customers">
        <IconButton onClick={handleOpen}>
          <Badge
            color="primary"
            badgeContent={activeFilterCount}
            invisible={activeFilterCount === 0}
          >
            <FilterListIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box p={2} width={300}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              applyFilters();
            }}
          >
            <Stack spacing={2}>
              <TextField
                size="small"
                label="Name"
                value={localFilters.name}
                onChange={(e) =>
                  setLocalFilters({ ...localFilters, name: e.target.value })
                }
              />
              <TextField
                size="small"
                label="Account Number"
                value={localFilters.account_number}
                onChange={(e) => {
                  // Only allow numeric input
                  const value = e.target.value.replace(/\D/g, '');
                  setLocalFilters({
                    ...localFilters,
                    account_number: value,
                  });
                }}
                inputProps={{
                  pattern: "[0-9]*"
                }}
                helperText="Enter numeric account number"
              />
              <TextField
                size="small"
                label="Mobile"
                value={localFilters.mobile}
                onChange={(e) =>
                  setLocalFilters({ ...localFilters, mobile: e.target.value })
                }
              />
              <TextField
                size="small"
                label="Email"
                value={localFilters.email}
                onChange={(e) =>
                  setLocalFilters({ ...localFilters, email: e.target.value })
                }
              />
              <TextField
                size="small"
                label="Address"
                value={localFilters.address}
                onChange={(e) =>
                  setLocalFilters({ ...localFilters, address: e.target.value })
                }
              />
              <TextField
                size="small"
                label="GST"
                value={localFilters.gst}
                onChange={(e) =>
                  setLocalFilters({ ...localFilters, gst: e.target.value })
                }
              />
              <FormControl fullWidth margin="normal">
                <InputLabel>Location</InputLabel>
                <Select
                  name="location"
                  value={localFilters.location}
                  onChange={(e) =>
                    setLocalFilters({
                      ...localFilters,
                      location: e.target.value,
                    })
                  }
                  label="Location"
                >
                  {locations.map((loc) => (
                    <MenuItem key={loc.id} value={loc.location}>
                      {loc.location}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button onClick={resetFilters}>Reset</Button>
                <Button type="submit" variant="contained">
                  Apply
                </Button>
              </Stack>
            </Stack>
          </form>
        </Box>
      </Popover>
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CustomerFilterPopover;
